// src/components/Layout/Header.js
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import "./Header.css";
import {
  FaEnvelopeOpen,
  FaMoon,
  FaSun,
  FaBell,
  FaChevronDown,
  FaUser,
  FaSignOutAlt,
  FaRegCheckCircle,
} from "react-icons/fa";
import { useTheme } from "../../context/ThemeContext";

const Header = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const rightRef = useRef(null);

  // Placeholder until a real notifications feed is wired up. The unread count
  // drives the badge; an empty list renders the "all caught up" state.
  const notifications = [];
  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate("/login");
  };

  const handleProfile = () => {
    setMenuOpen(false);
    navigate("/app/profile");
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (rightRef.current && !rightRef.current.contains(e.target)) {
        setMenuOpen(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const displayName = user?.name || "Analyst";
  const email = user?.email || "";
  const role = user?.role ? String(user.role).replace(/[_-]+/g, " ") : "Security Analyst";
  const initials = displayName
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="pm-header">
      <div className="pm-header-left">
        <div className="app-brand">
          <div className="app-logo">
            <span className="app-logo-badge">
              <FaEnvelopeOpen className="app-logo-icon" />
            </span>
            <span className="app-logo-text">PhishMatrix AI</span>
          </div>
          <div className="app-tagline">
            <span>by Accenture</span>
            <img src="/Accenture-Emblem.png" alt="Accenture" className="app-tagline-logo" />
          </div>
        </div>
      </div>

      <div className="pm-header-right" ref={rightRef}>
        <div className="pm-action-cluster">
          <button
            type="button"
            className="pm-icon-btn"
            onClick={toggleTheme}
            title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
            aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
          >
            {theme === "light" ? <FaMoon /> : <FaSun />}
          </button>

          <div className="pm-notif-wrap">
            <button
              type="button"
              className={`pm-icon-btn ${notifOpen ? "active" : ""}`}
              onClick={() => {
                setNotifOpen((v) => !v);
                setMenuOpen(false);
              }}
              aria-haspopup="true"
              aria-expanded={notifOpen}
              aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
            >
              <FaBell />
              {unreadCount > 0 && (
                <span className="pm-notif-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
              )}
            </button>

            {notifOpen && (
              <div className="pm-popover pm-notif-menu" role="dialog" aria-label="Notifications">
                <div className="pm-popover-head">
                  <span className="pm-popover-title">Notifications</span>
                  {unreadCount > 0 && <span className="pm-popover-count">{unreadCount} new</span>}
                </div>

                <div className="pm-notif-list">
                  {notifications.length === 0 ? (
                    <div className="pm-notif-empty">
                      <FaRegCheckCircle className="pm-notif-empty-icon" />
                      <p className="pm-notif-empty-title">You're all caught up</p>
                      <p className="pm-notif-empty-sub">No new notifications right now.</p>
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <button key={n.id} type="button" className={`pm-notif-item ${n.read ? "" : "unread"}`}>
                        <span className="pm-notif-item-dot" aria-hidden />
                        <span className="pm-notif-item-body">
                          <span className="pm-notif-item-title">{n.title}</span>
                          <span className="pm-notif-item-time">{n.time}</span>
                        </span>
                      </button>
                    ))
                  )}
                </div>

                <div className="pm-popover-foot">
                  <button type="button" className="pm-popover-link" disabled>
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <span className="pm-header-sep" aria-hidden />

        <div className="pm-user-wrap">
          <button
            type="button"
            className={`pm-user-chip ${menuOpen ? "active" : ""}`}
            onClick={() => {
              setMenuOpen((v) => !v);
              setNotifOpen(false);
            }}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            aria-label="Open user menu"
          >
            <span className="pm-avatar-circle">
              {initials}
              <span className="pm-avatar-status" aria-hidden />
            </span>
            <span className="pm-user-meta">
              <span className="pm-user-name" title={displayName}>
                {displayName}
              </span>
              <span className="pm-user-role">{role}</span>
            </span>
            <FaChevronDown className={`pm-user-caret ${menuOpen ? "open" : ""}`} />
          </button>

          {menuOpen && (
            <div className="pm-popover pm-user-menu" role="menu">
              <div className="pm-user-menu-head">
                <span className="pm-avatar-circle pm-avatar-lg">{initials}</span>
                <div className="pm-user-menu-id">
                  <span className="pm-user-menu-name">{displayName}</span>
                  {email && <span className="pm-user-menu-email" title={email}>{email}</span>}
                  <span className="pm-user-menu-rolepill">{role}</span>
                </div>
              </div>

              <div className="pm-user-menu-body">
                <button className="pm-user-menu-item" onClick={handleProfile} role="menuitem">
                  <FaUser className="pm-user-menu-icon" />
                  My Profile
                </button>
                <button
                  className="pm-user-menu-item destructive"
                  onClick={handleLogout}
                  role="menuitem"
                >
                  <FaSignOutAlt className="pm-user-menu-icon" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
